import { fmtMonthDayYear, fmtTimeZ } from '@concord/domain';
import { Button, Crumbs, Notice, PageHead, Panel, PanelHead } from '@concord/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import type { Me } from '../../lib/session';
import { ConsoleLayout } from '../console/ConsoleLayout';
import s from './audit.module.css';

interface Entry {
  id: string;
  at: string;
  actorId: string | null;
  actorKind: string;
  actorName: string | null;
  action: string;
  entityType: string | null;
  entityId: string | null;
  caseRef: string | null;
  method: string | null;
  path: string | null;
  status: number | null;
  ip: string | null;
  userAgent: string | null;
  detail: Record<string, unknown>;
}

/** Readable names for the actions an admin looks for most. Anything else shows its code. */
export const ACTION_LABEL: Record<string, string> = {
  'auth.login': 'Signed in',
  'auth.login_failed': 'Sign-in refused',
  'auth.logout': 'Signed out',
  'auth.idle_timeout': 'Signed out after 15 idle minutes',
  'case.viewed': 'Viewed a case',
  'case.panel_viewed': 'Viewed physician candidates',
  'case.intake_viewed': 'Viewed an intake',
  'case.removed_items_viewed': 'Viewed removed items',
  'case.reassigned': 'Reassigned a case',
  'cases.handed_over': 'Handed over open cases',
  'record.downloaded': 'Downloaded a record',
  'record.uploaded': 'Uploaded a record',
  'record.deleted': 'Removed a record',
  'record.restored': 'Restored a record',
  'record.updated': 'Edited a record',
  'guest.removed': 'Removed a guest',
  'guest.restored': 'Restored a guest',
  'account.viewed': 'Viewed an account',
  'account.updated': 'Changed an account',
  'account.admin_changed': 'Changed admin access',
  'account.profile_opened': 'Opened an account for editing',
  'account.profile_updated': 'Edited an account',
  'account.permissions_changed': 'Changed permissions',
  'mfa.reset': 'Removed an authenticator app',
  'account.invited': 'Invited an account',
  'portal.home_viewed': 'Opened their home page',
  'pii.revealed': 'Revealed a masked detail',
  'link.case_viewed': 'Opened a case by secure link',
  'link.record_viewed': 'Opened a record by secure link',
  'link.accepted': 'Accepted by secure link',
  'link.declined': 'Declined by secure link',
  'link.alternative_requested': 'Asked for another time by link',
  'link.invalid': 'Used an invalid link',
  'feedback.sent': 'Sent feedback',
  'feedback.viewed': 'Viewed feedback',
  'audit.viewed': 'Viewed the audit log',
};

const FILTERS: [string, string][] = [
  ['', 'All actions'],
  ['auth.login', 'Sign-ins'],
  ['auth.login_failed', 'Refused sign-ins'],
  ['auth.logout', 'Sign-outs'],
  ['auth.idle_timeout', 'Idle sign-outs'],
  ['case.viewed', 'Case views'],
  ['record.downloaded', 'Record downloads'],
  ['pii.revealed', 'Reveals'],
  ['case.reassigned', 'Reassignments'],
  ['record.deleted', 'Record removals'],
  ['record.restored', 'Restores'],
  ['account.updated', 'Account changes'],
  ['account.profile_updated', 'Account edits'],
  ['account.permissions_changed', 'Permission changes'],
];

const PAGE = 100;

function detailText(e: Entry): string {
  const d = e.detail;
  const parts: string[] = [];
  if (typeof d.field === 'string') parts.push(`field: ${d.field}`);
  if (typeof d.reason === 'string') parts.push(d.reason);
  if (typeof d.method === 'string') parts.push(`by ${d.method}`);
  if (Array.isArray(d.fields) && d.fields.length) parts.push(`changed: ${d.fields.join(', ')}`);
  if (typeof d.idleMinutes === 'number') parts.push(`idle ${d.idleMinutes} min`);
  if (e.status && e.status >= 400) parts.push(`refused (${e.status})`);
  return parts.join(' · ');
}

/** The audit trail: every sign-in, view, change and reveal. Super admins only. */
export function AuditPage({ me }: { me: Me }) {
  const [params, setParams] = useSearchParams();
  const [text, setText] = useState(params.get('q') ?? '');
  const q = params.get('q') ?? '';
  const action = params.get('action') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const offset = Number(params.get('offset') ?? 0);
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) v ? next.set(k, v) : next.delete(k);
    if (!('offset' in patch)) next.delete('offset');
    setParams(next);
  };
  const qs = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
  if (q) qs.set('q', q);
  if (action) qs.set('action', action);
  if (from) qs.set('from', new Date(`${from}T00:00:00`).toISOString());
  if (to) qs.set('to', new Date(`${to}T23:59:59`).toISOString());
  const res = useQuery({
    queryKey: ['audit', qs.toString()],
    queryFn: () => api<{ items: Entry[]; total: number }>(`/staff/audit?${qs}`),
  });
  const tz = me.timezone;
  const items = res.data?.items ?? [];
  const total = res.data?.total ?? 0;

  return (
    <ConsoleLayout me={me} current="audit">
      <Crumbs items={[{ label: 'Audit log' }]} />
      <PageHead
        title="Audit log"
        when="Every sign-in, sign-out, view of patient data, change and reveal, newest first. Super admins only. Entries cannot be edited or deleted."
      />
      <form
        className={s.filters}
        onSubmit={(e) => {
          e.preventDefault();
          set({ q: text.trim() || null });
        }}
      >
        <input
          className="input"
          aria-label="Search the audit log"
          placeholder="Case reference, name, account ID, IP or action"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <select
          className="select"
          aria-label="Action"
          value={action}
          onChange={(e) => set({ action: e.target.value || null })}
        >
          {FILTERS.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <label className={s.date}>
          From
          <input
            type="date"
            className="input"
            value={from}
            onChange={(e) => set({ from: e.target.value || null })}
          />
        </label>
        <label className={s.date}>
          To
          <input
            type="date"
            className="input"
            value={to}
            onChange={(e) => set({ to: e.target.value || null })}
          />
        </label>
        <Button type="submit" variant="sec">
          Search
        </Button>
      </form>
      {res.isError && <Notice tone="bad">{errorMessage(res.error)}</Notice>}
      <Panel aria-label="Audit entries">
        <PanelHead
          title="Entries"
          sub={res.data ? `${total.toLocaleString('en-US')} ${total === 1 ? 'entry' : 'entries'}` : ''}
        />
        <div className="tw">
          <table className={s.table}>
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>What</th>
                <th>Record</th>
                <th>From</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className={e.status && e.status >= 400 ? s.refused : undefined}>
                  <td className={s.when}>
                    {fmtMonthDayYear(new Date(e.at), tz)}
                    <div className="sub2">{fmtTimeZ(new Date(e.at), tz)}</div>
                  </td>
                  <td>
                    {e.actorName ?? (e.actorKind === 'anonymous' ? 'Not signed in' : 'System')}
                    <div className="sub2">{e.actorKind}</div>
                  </td>
                  <td>
                    {ACTION_LABEL[e.action] ?? e.action}
                    <div className={s.code}>{e.action}</div>
                  </td>
                  <td>
                    {e.caseRef && <span className="ref">{e.caseRef}</span>}
                    {e.entityType && e.entityType !== 'case' && (
                      <div className="sub2">
                        {e.entityType}
                        {e.entityId && e.entityId !== e.caseRef ? ` ${e.entityId.slice(0, 8)}` : ''}
                      </div>
                    )}
                  </td>
                  <td>
                    {e.ip ?? '—'}
                    {e.userAgent && (
                      <div className="sub2" title={e.userAgent}>
                        {e.userAgent.slice(0, 40)}
                        {e.userAgent.length > 40 ? '…' : ''}
                      </div>
                    )}
                  </td>
                  <td className={s.detail}>{detailText(e)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {res.data && items.length === 0 && <div className="pbody sub2">No entries match.</div>}
        {total > PAGE && (
          <div className={s.pager}>
            <Button
              variant="sec"
              disabled={offset === 0}
              onClick={() => set({ offset: String(Math.max(0, offset - PAGE)) })}
            >
              Newer
            </Button>
            <span className="sub2">
              {offset + 1}–{Math.min(offset + PAGE, total)} of {total.toLocaleString('en-US')}
            </span>
            <Button
              variant="sec"
              disabled={offset + PAGE >= total}
              onClick={() => set({ offset: String(offset + PAGE) })}
            >
              Older
            </Button>
          </div>
        )}
      </Panel>
    </ConsoleLayout>
  );
}
